import React, {
  useState, useContext, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@mui/material';
import format from 'string-template';

import { CardsContainer } from '../../../../components/v2/Card';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useToast from '../../../hooks/useToast';
import LoadingOverlay from '../../../components/LoadingOverlay';
import ConfirmationModal from '../../../../components/v2/ConfirmationModal';
import { isInsideSalesAdmin } from '../../../../utils/postHog';
import LoggedInUserContext from '../../../../context/LoggedInUserContext';
import CoachHistory, { HistoryEventType } from '../../../Model/CoachHistory';
import Product from '../../../../Model/Product';
import { StyledButton } from './styles';
import texts from './texts.json';

const NonISCoachView = ({ coachDoc, isCAM }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [note, setNote] = useState('');
  const [hasProducts, setHasProducts] = useState(null);
  const [hasProductsError, setHasProductsError] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { userId, userDoc } = useContext(LoggedInUserContext);
  const isReadOnly = !isInsideSalesAdmin();

  useEffect(() => {
    const checkProducts = async () => {
      if (!coachDoc?.id) return;
      try {
        const productsCollection = await Product.getProductByCoachId(coachDoc.id);
        const hasEligibleProducts = productsCollection.docs
          ?.some((product) => !product.isArchived && !product.isHidden) ?? false;
        if (isComponentMountedRef.current) {
          setHasProducts(hasEligibleProducts);
          setHasProductsError(false);
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          setHasProducts(false);
          setHasProductsError(true);
        }
        showToast(format(texts.loadProductsError, { error: error.message }), { error: true });
      }
    };
    checkProducts();
  }, [coachDoc, isComponentMountedRef, showToast]);

  const handleCloseConfirmation = useCallback(() => {
    setShowConfirmation(false);
    setNote('');
  }, []);

  const onISEnable = async () => {
    setShowConfirmation(false);
    setIsSaving(true);
    try {
      await coachDoc.updateISEnrollment(true);
      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.IS_FLAG_UPDATED,
        coachId: coachDoc.id,
        actionedBy: userId,
        actionedByUserName: userDoc?.name,
        metadata: {
          previousValue: false,
          newValue: true,
          ...(note && { note }),
        },
      });
      showToast(texts.enabled, { type: 'success' });
    } catch (error) {
      showToast(format(texts.enableISError, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
        setNote('');
      }
    }
  };

  let alertMessage = texts.note;
  if (hasProductsError) {
    alertMessage = texts.productLoadFailureNote;
  } else if (hasProducts === false) {
    alertMessage = texts.noProductsNote;
  }

  return (
    <CardsContainer $fullWidth>
      <Alert severity={hasProductsError ? 'error' : 'warning'}>
        {alertMessage}
      </Alert>
      {
       !isCAM && (
       <StyledButton onClick={() => setShowConfirmation(true)} disabled={isReadOnly || !hasProducts}>
         {texts.enroll}
       </StyledButton>
       )
      }
      <LoadingOverlay isLoading={isSaving} />
      {showConfirmation && (
        <ConfirmationModal
          open
          onClose={handleCloseConfirmation}
          onConfirm={onISEnable}
          title={format(texts.confirmationTitle, { coachName: coachDoc.name })}
          description={format(texts.confirmationMessage, { coachName: coachDoc.name })}
          showAdditionalInput
          onInputChange={setNote}
        />
      )}
    </CardsContainer>
  );
};

NonISCoachView.propTypes = {
  coachDoc: PropTypes.object,
  isCAM: PropTypes.bool,
};

NonISCoachView.defaultProps = {
  coachDoc: null,
  isCAM: false,
};

export default NonISCoachView;
